import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

import {
  listTickets,
  createStripeTicketCheckoutSession,
  getUserEmail,
  getUnconfirmedPayPayPayment,
} from "utils/api";
import ButtonDialog from "../components/ButtonDialog";
import Loading from "../components/Loading";
import { COLORS } from "constants/colors";
import AppLayout from "../components/AppLayout";
import Error from "../components/Error";
import InputEmail from "../components/InputEmail";
import PayPayButton from "../components/PayPayButton";
import StyledButton from "../components/StyledButton";
import TicketBox from "../components/TicketBox";
import { lineAccountLinks } from "constants/links";
import { useUrlParams } from "hooks/useUrlParams";
import { useTheme } from "@emotion/react";

// Constants
const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const PURCHASE_METHODS = {
  STRIPE: "stripe",
  PAYPAY: "paypay",
};

// Stripe initialization - outside component to avoid recreation
const stripePromise = loadStripe(STRIPE_API_KEY);

// Custom hooks
const useTickets = (soulId) => {
  const [state, setState] = useState({
    tickets: [],
    isLoading: true,
    isError: false,
    merchantPaymentId: null,
    showPaymentCheck: false,
  });

  const checkUnconfirmedPayment = useCallback(async (soulId) => {
    try {
      const { merchant_payment_id: merchantPaymentId } =
        await getUnconfirmedPayPayPayment(soulId);

      if (merchantPaymentId) {
        setState((prev) => ({
          ...prev,
          merchantPaymentId,
          showPaymentCheck: true,
        }));
      }
    } catch (error) {
      console.error("Error checking unconfirmed payment:", error);
    }
  }, []);

  const loadTickets = useCallback(async (soulId) => {
    try {
      const tickets = await listTickets(soulId);
      setState((prev) => ({ ...prev, tickets, isError: false }));
    } catch (error) {
      console.error("Error loading tickets:", error);
      setState((prev) => ({ ...prev, isError: true }));
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  const initializeTickets = useCallback(
    async (soulId) => {
      if (!soulId) return;

      await checkUnconfirmedPayment(soulId);
      await loadTickets(soulId);
    },
    [checkUnconfirmedPayment, loadTickets]
  );

  useEffect(() => {
    initializeTickets(soulId);
  }, [initializeTickets, soulId]);

  return state;
};

const usePurchase = (soulId, work) => {
  const navigate = useNavigate();
  const [state, setState] = useState({
    isProcessing: false,
    selectedEpisodeId: null,
    selectedTicketKey: null,
    purchaseMethod: null,
    emailRequired: false,
  });

  const purchaseWithStripe = useCallback(
    async (episodeId, ticketKey) => {
      try {
        const { session_id } = await createStripeTicketCheckoutSession(
          soulId,
          episodeId,
          ticketKey
        );
        const stripe = await stripePromise;

        const { error } = await stripe.redirectToCheckout({
          sessionId: session_id,
        });

        if (error) {
          console.error("Stripe error:", error);
        }
      } catch (error) {
        console.error("Purchase error:", error);
      }
    },
    [soulId]
  );

  const purchaseWithPayPay = useCallback(
    (episodeId, ticketKey) => {
      navigate(
        `/${work}/paypay/ticket-confirm?soul_id=${soulId}&episode_id=${episodeId}&ticket_key=${ticketKey}`
      );
    },
    [navigate, soulId, work]
  );

  const handlePurchaseMethod = useCallback(
    async (method, episodeId, ticketKey) => {
      setState((prev) => ({ ...prev, isProcessing: true }));

      try {
        const email = await getUserEmail();

        if (!email.email_address) {
          setState((prev) => ({
            ...prev,
            emailRequired: true,
            purchaseMethod: method,
            selectedEpisodeId: episodeId,
            selectedTicketKey: ticketKey,
            isProcessing: false,
          }));
          window.scrollTo(0, 0);
          return;
        }

        if (method === PURCHASE_METHODS.STRIPE) {
          await purchaseWithStripe(episodeId, ticketKey);
        } else if (method === PURCHASE_METHODS.PAYPAY) {
          purchaseWithPayPay(episodeId, ticketKey);
        }
      } catch (error) {
        console.error("Purchase method error:", error);
      } finally {
        setState((prev) => ({ ...prev, isProcessing: false }));
      }
    },
    [purchaseWithStripe, purchaseWithPayPay]
  );

  const navigateToPurchase = useCallback(async () => {
    const { purchaseMethod, selectedEpisodeId, selectedTicketKey } = state;

    if (purchaseMethod === PURCHASE_METHODS.STRIPE) {
      await purchaseWithStripe(selectedEpisodeId, selectedTicketKey);
    } else if (purchaseMethod === PURCHASE_METHODS.PAYPAY) {
      purchaseWithPayPay(selectedEpisodeId, selectedTicketKey);
    }
  }, [state, purchaseWithStripe, purchaseWithPayPay]);

  return {
    ...state,
    handlePurchaseMethod,
    navigateToPurchase,
    setPurchaseState: setState,
  };
};

// Component for Story Section
const StorySection = ({ characterName, type, lines }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      width: "100%",
    }}
  >
    <Typography
      sx={(theme) => ({
        ...theme.components.sectionHeader,
      })}
    >
      {type === "epilogue" ? "Epilogue" : "Story"}
    </Typography>

    <Typography
      sx={(theme) => ({
        mt: 1,
        ...theme.components.storySection.name,
      })}
    >
      - {characterName} -
    </Typography>

    <Typography
      sx={(theme) => ({
        mt: 2,
        textAlign: "center",
        letterSpacing: "0.1em",
        ...theme.components.storySection.story,
      })}
    >
      {lines.map((line, index, array) => (
        <React.Fragment key={index}>
          {line}
          {index !== array.length - 1 && <br />}
        </React.Fragment>
      ))}
    </Typography>
  </Box>
);

// Component for Others Section
const OthersSection = ({ accountLinks }) => {
  if (accountLinks.length === 0) return null;

  return (
    <Box
      sx={{
        width: "100%",
        py: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        mt: 6,
      }}
    >
      <Typography
        sx={(theme) => ({
          letterSpacing: "0.06em",
          mb: 3,
          ...theme.components.sectionHeader,
        })}
      >
        Others
      </Typography>
      {accountLinks.map((char, idx, arr) => (
        <Typography
          key={char.name}
          component="a"
          href={char.url}
          sx={{
            color: COLORS.TEXT_WHITE,
            fontFamily: '"Noto Serif JP", serif',
            fontSize: "24px",
            fontWeight: "bold",
            textDecoration: "none",
            mb: idx === arr.length - 1 ? 0 : 2,
            cursor: "pointer",
            transition: "opacity 0.2s",
            "&:hover": { opacity: 0.7 },
          }}
        >
          {char.displayName}
        </Typography>
      ))}
    </Box>
  );
};

// Component for Purchase Dialog
const PurchaseDialog = ({
  open,
  onClose,
  onPurchaseMethod,
  isProcessing,
  lng,
  t,
}) => {
  const stripeButton = (
    <StyledButton
      key="credit-card"
      variant="contained"
      onClick={() => onPurchaseMethod(PURCHASE_METHODS.STRIPE)}
      sx={(theme) => ({
        width: "100%",
        ...theme.components.button,
        // fontSize: "16px",
        // color: theme.Dialog.button.color,
        // border: `2px solid ${theme.Dialog.button.color}`,
        // background: isProcessing ? "white" : "transparent",
      })}
      disabled={isProcessing}
    >
      {t("common:button.purchase_option_credit_card")}
    </StyledButton>
  );

  const payPayBox = (
    <Box key="paypay" sx={{ width: "100%" }}>
      <PayPayButton
        onClick={() => onPurchaseMethod(PURCHASE_METHODS.PAYPAY)}
        disabled={isProcessing}
      />
      <Typography
        sx={(theme) => ({
          mt: 1,
          textAlign: "center",
          whiteSpace: "pre-line",
          fontSize: "12px",
        })}
      >
        {t("common:button.purchase_option_paypay")}
      </Typography>
    </Box>
  );

  const rawButtons = lng === "ja" ? [stripeButton, payPayBox] : [stripeButton];

  return (
    <ButtonDialog
      open={open}
      title={t("common:button.dialog.select_purchase")}
      rawButtons={rawButtons}
      handleClose={onClose}
    />
  );
};

// Component for Ticket Section
const TicketSection = ({ tickets, onTicketPurchase, t }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 5,
      width: "100%",
    }}
  >
    <Typography
      sx={(theme) => ({
        mt: 8,
        ...theme.components.sectionHeader,
      })}
    >
      Ticket
    </Typography>

    {tickets.length === 0 ? (
      <Typography
        sx={(theme) => ({
          whiteSpace: "pre-line",
          lineHeight: 2.0,
          fontFamily: theme.system.fonts.body,
          color: theme.system.colors.text.primary,
          fontSize: theme.system.fontSizes.medium,
        })}
      >
        {t("common:error.ticket_not_available")}
      </Typography>
    ) : (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          width: "100%",
        }}
      >
        {tickets.map((ticket) => (
          <TicketBox
            key={ticket.ticket_key}
            ticket={ticket}
            onClick={() =>
              onTicketPurchase(ticket.episode_id, ticket.ticket_key)
            }
          />
        ))}
      </Box>
    )}
  </Box>
);

// Component for Chat Section
const ChatSection = ({ gifUrl }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 3, // Reduced gap from 5 to 3
      width: "100%",
    }}
  >
    <Typography
      sx={(theme) => ({
        mt: 8,
        ...theme.components.sectionHeader,
      })}
    >
      Chat
    </Typography>
    <Box
      component="img"
      src={gifUrl}
      alt="Chat Preview"
      sx={{
        width: "100%", // Full width on mobile
        borderRadius: 0, // No border radius for edge-to-edge display
      }}
    />
  </Box>
);

// Main Component
function Tickets() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { soulId, lng, work, character } = useUrlParams();
  const { t } = useTranslation([work, "common"]);

  const { tickets, isLoading, isError, merchantPaymentId, showPaymentCheck } =
    useTickets(soulId);

  const {
    isProcessing,
    emailRequired,
    handlePurchaseMethod,
    navigateToPurchase,
    setPurchaseState,
  } = usePurchase(soulId, work);

  const [dialogState, setDialogState] = useState({
    openDialog: false,
    selectedEpisodeId: null,
    selectedTicketKey: null,
  });

  // Computed values
  const isRepurchase = useMemo(
    () => tickets.some((ticket) => ticket.ticket_key.includes("repurchase")),
    [tickets]
  );

  const storyData = useMemo(() => {
    const characterName = t(`${work}:character.${character}`);
    const type = isRepurchase ? "epilogue" : "story";
    const lines = t(`${work}:${type}.${character}`, {
      returnObjects: true,
    });

    return { characterName, type, lines };
  }, [work, character, isRepurchase, t]);

  const otherAccountLinks = useMemo(() => {
    return Object.entries(lineAccountLinks[work]?.[lng] || {})
      .filter(([key]) => key !== character)
      .map(([name, url]) => ({
        name,
        url,
        displayName: t(`${work}:character.${name}`),
      }));
  }, [work, character, lng, t]);

  // Event handlers
  const handleTicketPurchase = useCallback((episodeId, ticketKey) => {
    setDialogState({
      openDialog: true,
      selectedEpisodeId: episodeId,
      selectedTicketKey: ticketKey,
    });
  }, []);

  const handleDialogPurchaseMethod = useCallback(
    (method) => {
      const { selectedEpisodeId, selectedTicketKey } = dialogState;
      handlePurchaseMethod(method, selectedEpisodeId, selectedTicketKey);
      setDialogState((prev) => ({ ...prev, openDialog: false }));
    },
    [dialogState, handlePurchaseMethod]
  );

  const handlePaymentCheckClose = useCallback(() => {
    // This would need to be implemented in the useTickets hook
    // For now, we'll just navigate or close
  }, []);

  const navigateToPaymentCheck = useCallback(() => {
    navigate(
      `/${work}/paypay/check?soul_id=${soulId}&merchant_payment_id=${merchantPaymentId}&lng=${lng}`
    );
  }, [navigate, work, soulId, merchantPaymentId, lng]);

  // Render loading state
  if (isLoading) {
    return (
      <AppLayout page="Tickets" width="100%">
        <Loading />
      </AppLayout>
    );
  }

  // Render error state
  if (isError) {
    return (
      <AppLayout page="Tickets" width="100%">
        <Error />
      </AppLayout>
    );
  }

  // Render email input state
  if (emailRequired) {
    return (
      <AppLayout page="Tickets" width="88%">
        <InputEmail soulId={soulId} onClickPurchase={navigateToPurchase} />
      </AppLayout>
    );
  }

  // Main render
  return (
    <AppLayout page="Tickets" width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
          width: "100%",
          mb: 5,
        }}
      >
        {/* Hero Image */}
        <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
          <img
            src={theme.assets.heroImage}
            alt="hero image"
            width="100%"
            style={{ display: "block" }}
          />
        </Box>

        {/* Story Section */}
        <StorySection
          characterName={storyData.characterName}
          type={storyData.type}
          lines={storyData.lines}
        />

        {/* Chat Section */}
        {work === "yofukashi" && character === "seri" && (
          <ChatSection gifUrl="/yofukashi/chat_demo_seri.gif" />
        )}

        {/* Tickets Section */}
        <TicketSection
          tickets={tickets}
          onTicketPurchase={handleTicketPurchase}
          t={t}
        />

        {/* Others Section */}
        <OthersSection accountLinks={otherAccountLinks} />
      </Box>

      {/* Payment Check Dialog */}
      <ButtonDialog
        open={showPaymentCheck}
        title="PayPay購入による処理が正常に完了していない可能性があります。"
        handleClose={handlePaymentCheckClose}
        buttons={[
          {
            label: "購入状況を確認",
            onClick: navigateToPaymentCheck,
          },
        ]}
      />

      {/* Purchase Method Dialog */}
      <PurchaseDialog
        open={dialogState.openDialog}
        onClose={() =>
          setDialogState((prev) => ({ ...prev, openDialog: false }))
        }
        onPurchaseMethod={handleDialogPurchaseMethod}
        isProcessing={isProcessing}
        lng={lng}
        t={t}
      />
    </AppLayout>
  );
}

export default Tickets;
